import { ChangeDetectorRef } from '@angular/core';
import { sortBy } from 'lodash-es';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ORG_NAME_ACO_REACH_IL, ORG_NAME_MHN_ACO_AETNA } from 'src/app/core/constants/organization';
import { JOB_TITLE_CMHC_BH_CM, ROLE_EXTERNAL_AGENCY, ROLE_HOSPITAL_USER } from 'src/app/core/constants/user-management';
import { Role } from 'src/app/core/models/entitlements/role';
import { HospitalUserFacilityGroup } from 'src/app/core/models/hospital-user-facility-group';
import { NewUserRequestForm } from 'src/app/core/models/new-user-request-form';
import { Organization } from 'src/app/core/models/organization';
import { ProviderGroup } from 'src/app/core/models/provider-group';
import { ReferenceTable } from 'src/app/core/models/reference-table';
import { User } from 'src/app/core/models/user';
import {
  USER_AFFILIATION_TYPE_GLOBAL,
  USER_AFFILIATION_TYPE_PROVIDERGROUP,
  USER_AFFILIATION_TYPE_TEMPLATE,
  UserAffiliation
} from 'src/app/core/models/user-affiliation';
import { UserAffiliationType } from 'src/app/core/models/user-affiliation-type';
import { UserEmployer } from 'src/app/core/models/user-employer';
import { RoleService } from 'src/app/core/services/role.service';
import { UserService } from 'src/app/core/services/user.service';

export abstract class AffiliationBase {
  USER_AFFILIATION_TYPE_GLOBAL: string = USER_AFFILIATION_TYPE_GLOBAL;
  USER_AFFILIATION_TYPE_PROVIDERGROUP: string = USER_AFFILIATION_TYPE_PROVIDERGROUP;
  USER_AFFILIATION_TYPE_TEMPLATE: string = USER_AFFILIATION_TYPE_TEMPLATE;
  ROLE_HOSPITAL_USER: string = ROLE_HOSPITAL_USER;

  selectedProviderGroupIds: Set<string> = new Set();
  selectedProviderGroups: ProviderGroup[] = [];
  selectedProviderGroupsReachIL: ProviderGroup[] = [];
  selectedProviderGroupIdsReachIL: Set<string> = new Set();
  selectedProviderGroupsAetna: ProviderGroup[] = [];
  selectedProviderGroupIdsAetna: Set<string> = new Set();

  selectedFacilityGroupIds: Set<number> = new Set();
  selectedFacilityGroupIdsReachIL: Set<number> = new Set();
  selectedFacilityGroupIdsAetna: Set<number> = new Set();
  newUserRequestForm: NewUserRequestForm = new NewUserRequestForm();
  user: User = new User();

  affiliation: UserAffiliation = null;
  affiliationOptions: UserAffiliation[];
  providerGroups: ProviderGroup[];

  affiliationReachIL: UserAffiliation = null;
  affiliationOptionsReachIL: UserAffiliation[] = [];
  providerGroupsReachIL: ProviderGroup[];

  affiliationAetna: UserAffiliation = null;
  affiliationOptionsAetna: UserAffiliation[] = [];
  providerGroupsAetna: ProviderGroup[];

  userRolesList: Role[];
  hospitalUserFacilityGroups: HospitalUserFacilityGroup[];
  employers: UserEmployer[];
  jobTitles: ReferenceTable[];

  employersReachIL: UserEmployer[];
  jobTitlesReachIL: ReferenceTable[];

  employersAetna: UserEmployer[];
  jobTitlesAetna: ReferenceTable[];

  userRolesListReachIL: Role[];
  userRolesListAetna: Role[];

  loadingCurrentOrganizationConfig: boolean;
  loadingReachILOrganizationConfig: boolean;
  excludedRoles = new Set(['Super Administrator', 'CareManager Supervisor', 'Care Giver']);
  showSudSmiAccess: boolean;

  get showOtherJobTitle(): boolean {
    return this.user?.jobTitle === 'Other';
  }

  constructor(protected cdr: ChangeDetectorRef) {}

  onAffiliationTypeChange(_value: string) {
    this.selectedProviderGroups = [];
    this.selectedProviderGroupIds.clear();
  }

  onAffiliationTypeChangeReachIL(_value: string) {
    this.selectedProviderGroupsReachIL = [];
    this.selectedProviderGroupIdsReachIL.clear();
  }

  onAffiliationTypeChangeAcoAetna(_value: string) {
    this.selectedProviderGroupsAetna = [];
    this.selectedProviderGroupIdsAetna.clear();
  }

  onAllListLoaded() {
    // Override if additional processing is required
  }

  loadCurrentOrganization(
    selectedOrg: Organization,
    roleService: RoleService,
    userService: UserService,
    isNewUserRequest = false
  ) {
    const getUserAffiliationTypes = userService.getUserAffiliationTypes(
      selectedOrg.orgId,
      selectedOrg.orgLevel,
      isNewUserRequest
    );
    const fetchRoles = roleService.getOrgRoles(selectedOrg.orgId, selectedOrg.orgLevel);
    const fetchJobTitles = userService.getJobTitles(selectedOrg.orgId, selectedOrg.orgLevel);
    const fetchEmployers = userService.getUserEmployers(selectedOrg.orgId, selectedOrg.orgLevel);
    const fetchHospitalUserFacilityGroup = roleService.getHospitalUserFacilityGroups(
      selectedOrg.orgId,
      selectedOrg.orgLevel
    );

    this.loadingCurrentOrganizationConfig = true;

    this.cdr.detectChanges();

    forkJoin([getUserAffiliationTypes, fetchRoles, fetchJobTitles, fetchEmployers, fetchHospitalUserFacilityGroup])
      .pipe(
        finalize(() => {
          this.loadingCurrentOrganizationConfig = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        ([affiliationTypes, roles, jobTitles, employers, hospitalUserFacilityGroupList]: [
          UserAffiliationType[],
          Role[],
          ReferenceTable[],
          UserEmployer[],
          HospitalUserFacilityGroup[]
        ]) => {
          this.initializeAffiliationOptions(affiliationTypes);

          this.userRolesList = isNewUserRequest
            ? roles.filter((r) => !this.excludedRoles.has(r.roleDescription))
            : roles;

          this.jobTitles = jobTitles;
          this.employers = employers;
          this.hospitalUserFacilityGroups = hospitalUserFacilityGroupList;

          this.onAllListLoaded();
        }
      );
  }

  loadAdditionalOrganization(
    selectedOrg: Organization,
    roleService: RoleService,
    userService: UserService,
    isNewUserRequest = false
  ) {
    const getUserAffiliationTypes = userService.getUserAffiliationTypes(
      selectedOrg.orgId,
      selectedOrg.orgLevel,
      isNewUserRequest
    );
    const fetchRoles = roleService.getOrgRoles(selectedOrg.orgId, selectedOrg.orgLevel);
    const fetchJobTitles = userService.getJobTitles(selectedOrg.orgId, selectedOrg.orgLevel);
    const fetchEmployers = userService.getUserEmployers(selectedOrg.orgId, selectedOrg.orgLevel);

    this.loadingReachILOrganizationConfig = true;

    forkJoin([getUserAffiliationTypes, fetchRoles, fetchJobTitles, fetchEmployers])
      .pipe(
        finalize(() => {
          this.loadingReachILOrganizationConfig = false;
        })
      )
      .subscribe(
        ([affiliationTypes, roles, jobTitles, employers]: [
          UserAffiliationType[],
          Role[],
          ReferenceTable[],
          UserEmployer[]
        ]) => {
          if (selectedOrg.orgName === ORG_NAME_MHN_ACO_AETNA) {
            this.initializeAffiliationOptionsACOAetna(affiliationTypes);
            this.userRolesListAetna = isNewUserRequest
              ? roles.filter((r) => !this.excludedRoles.has(r.roleDescription))
              : roles;

            this.jobTitlesAetna = jobTitles;
            this.employersAetna = employers;
          }

          if (selectedOrg.orgName === ORG_NAME_ACO_REACH_IL) {
            this.initializeAffiliationOptionsACOReachIL(affiliationTypes);
            this.userRolesListReachIL = isNewUserRequest
              ? roles.filter((r) => !this.excludedRoles.has(r.roleDescription))
              : roles;

            this.jobTitlesReachIL = jobTitles;
            this.employersReachIL = employers;
          }

          this.onAllListLoaded();
        }
      );
  }

  onUserRoleChange(_role: string) {
    this.selectedFacilityGroupIds.clear();

    this.recalculateSudSmiFields();
  }

  onUserRoleChangeReachIL(_role: string) {
    this.selectedFacilityGroupIdsReachIL.clear();
  }

  onJobTitleChange(_jobTitle: string) {
    this.user.otherJobTitle = null;

    this.recalculateSudSmiFields();
  }

  initializeAffiliationOptions(affiliationTypes: UserAffiliationType[], userId: number = null) {
    this.affiliationOptions = [];
    this.providerGroups = [];

    (affiliationTypes || []).forEach((affiliationType) => {
      if (affiliationType.type === USER_AFFILIATION_TYPE_TEMPLATE) {
        (affiliationType.templates || []).forEach((template) => {
          this.affiliationOptions.push({
            userId: userId,
            affiliationType: affiliationType.type,
            providerGroupIds: [],
            sftpProviderGroupIds: [],
            description: template.description,
            templateId: template.templateId
          });
        });
      } else {
        if (affiliationType.type === USER_AFFILIATION_TYPE_PROVIDERGROUP) {
          this.providerGroups.push(...affiliationType.providerGroups);
        }

        this.affiliationOptions.push({
          userId: userId,
          affiliationType: affiliationType.type,
          providerGroupIds: [],
          sftpProviderGroupIds: [],
          description: affiliationType.description,
          templateId: null
        });
      }
    });

    sortBy(this.affiliationOptions, [(affiliationOption) => affiliationOption.description]);
  }

  initializeAffiliationOptionsACOReachIL(affiliationTypes: UserAffiliationType[], userId: number = null) {
    this.affiliationOptionsReachIL = [];
    this.providerGroupsReachIL = [];

    (affiliationTypes || []).forEach((affiliationType) => {
      if (affiliationType.type === USER_AFFILIATION_TYPE_TEMPLATE) {
        (affiliationType.templates || []).forEach((template) => {
          this.affiliationOptionsReachIL.push({
            userId: userId,
            affiliationType: affiliationType.type,
            providerGroupIds: [],
            sftpProviderGroupIds: [],
            description: template.description,
            templateId: template.templateId
          });
        });
      } else {
        if (affiliationType.type === USER_AFFILIATION_TYPE_PROVIDERGROUP) {
          this.providerGroupsReachIL.push(...affiliationType.providerGroups);
        }

        this.affiliationOptionsReachIL.push({
          userId: userId,
          affiliationType: affiliationType.type,
          providerGroupIds: [],
          sftpProviderGroupIds: [],
          description: affiliationType.description,
          templateId: null
        });
      }
    });

    if (this.providerGroupsReachIL.length === 0) {
      this.affiliationOptionsReachIL = [];
    }

    sortBy(this.affiliationOptionsReachIL, [(affiliationOption) => affiliationOption.description]);
  }

  initializeAffiliationOptionsACOAetna(affiliationTypes: UserAffiliationType[], userId: number = null) {
    this.affiliationOptionsAetna = [];
    this.providerGroupsAetna = [];

    (affiliationTypes || []).forEach((affiliationType) => {
      if (affiliationType.type === USER_AFFILIATION_TYPE_TEMPLATE) {
        (affiliationType.templates || []).forEach((template) => {
          this.affiliationOptionsAetna.push({
            userId: userId,
            affiliationType: affiliationType.type,
            providerGroupIds: [],
            sftpProviderGroupIds: [],
            description: template.description,
            templateId: template.templateId
          });
        });
      } else {
        if (affiliationType.type === USER_AFFILIATION_TYPE_PROVIDERGROUP) {
          this.providerGroupsAetna.push(...affiliationType.providerGroups);
        }

        this.affiliationOptionsAetna.push({
          userId: userId,
          affiliationType: affiliationType.type,
          providerGroupIds: [],
          sftpProviderGroupIds: [],
          description: affiliationType.description,
          templateId: null
        });
      }
    });

    if (this.providerGroupsAetna.length === 0) {
      this.affiliationOptionsAetna = [];
    }

    sortBy(this.affiliationOptionsAetna, [(affiliationOption) => affiliationOption.description]);
  }

  public get isRoleExternalAgency(): boolean {
    return (
      this.newUserRequestForm.role?.roleDescription === ROLE_EXTERNAL_AGENCY ||
      this.user.role?.roleDescription === ROLE_EXTERNAL_AGENCY
    );
  }

  public get isJobTitleCMHCBHCM(): boolean {
    return this.newUserRequestForm.jobTitle === JOB_TITLE_CMHC_BH_CM || this.user.jobTitle === JOB_TITLE_CMHC_BH_CM;
  }

  public get isCurrentRoleExternalAgencyAndJobTitleCMHCBHCM(): boolean {
    return this.isRoleExternalAgency && this.isJobTitleCMHCBHCM;
  }

  private recalculateSudSmiFields() {
    this.showSudSmiAccess = this.isRoleExternalAgency;
    this.user.sudSmiProgramDashboardAccess = this.isCurrentRoleExternalAgencyAndJobTitleCMHCBHCM;
    this.newUserRequestForm.sudSmiProgramDashboard = this.isCurrentRoleExternalAgencyAndJobTitleCMHCBHCM;
  }
}
